import { useEffect, useState } from 'react';

import Table from './Tables';
import { useAlert } from './AlertContext';
import useTablePagination from '../Hooks/useTablePagination';
import useLatestRequest from '../Hooks/useLatestRequest';
import { useInformationPackageApi } from '../Services/InformationPackageApiProvider';
import type { InformationPackageFeatureApi } from '../Services/InformationPackageApiProviderTypes';

type FeatureFilterValue = string | string[] | null;

interface InformationPackageFeaturesTableProps {
  unitId: string;
  artifactKey?: string;
  title?: string;
  errorMessage?: string;
  featureOptions?: string[];
}

function getFeatureContentPreview(content: string, maxLength: number = 50): string {
  const text = content.replace(/[<>]/g, '').trim();

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength)}...`;
}

function getFeatureFilterValue(value: FeatureFilterValue): string[] | undefined {
  if (Array.isArray(value)) {
    return value.length ? value : undefined;
  }

  return value ? [value] : undefined;
}

// The feature kinds the extraction pipeline produces, in the order it runs them
// — see the feature list in PackageService.extract_features. These are the stored
// names the API filters on, so only the dropdown label is prettified.
const FEATURE_OPTIONS = ['Axiom', 'Rule', 'Concept', 'ConceptRelation'];

const FEATURE_LABELS: Record<string, string> = {
  ConceptRelation: 'Concept relation',
};

const featuresColumns = [
  {
    name: 'Content',
    selector: (row: InformationPackageFeatureApi) => getFeatureContentPreview(row.definition),
    sortable: false,
  },
  {
    name: 'Type',
    selector: (row: InformationPackageFeatureApi) => FEATURE_LABELS[row.name] ?? row.name,
    sortable: false,
  },
];

function InformationPackageFeaturesTable({
  unitId,
  artifactKey,
  title = 'Features',
  errorMessage = 'Failed to load information package features',
  featureOptions = FEATURE_OPTIONS,
}: InformationPackageFeaturesTableProps) {
  const { showAlert } = useAlert();
  const { getInformationPackageUnitFeatures } = useInformationPackageApi();

  const [features, setFeatures] = useState<InformationPackageFeatureApi[]>([]);
  const [featuresTotal, setFeaturesTotal] = useState(0);
  const [featureFilter, setFeatureFilter] = useState<string[] | undefined>();
  const [loading, setLoading] = useState(false);

  const tableStorageKey = artifactKey
    ? 'information_package_artifact_features'
    : 'information_package_unit_features';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableStorageKey);
  const beginRequest = useLatestRequest();

  const searchOptions = featureOptions.map((option) => ({
    label: FEATURE_LABELS[option] ?? option,
    value: option,
  }));

  const fetchFeatures = async (skip: number, limit: number, filter: string[] | undefined) => {
    const isLatest = beginRequest();
    setLoading(true);

    try {
      const response = await getInformationPackageUnitFeatures(unitId, skip, limit, {
        name: filter,
        artifactKey,
      });
      if (!isLatest()) return;

      setFeatures(response.items);
      setFeaturesTotal(response.totalCount);
    } catch {
      if (!isLatest()) return;
      showAlert('fail', errorMessage);
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  const handleFeatureFilterChange = async (
    skip: number,
    limit: number,
    value: FeatureFilterValue,
  ) => {
    const filter = getFeatureFilterValue(value);

    setFeatureFilter(filter);
    await fetchFeatures(skip, limit, filter);
  };

  useEffect(() => {
    fetchFeatures(storedSkip(), storedLimit(), featureFilter);
  }, [unitId, artifactKey]);

  return (
    <>
      <h2 className="mb-1">{title}</h2>
      <Table
        title=""
        storageKey={tableStorageKey}
        columns={featuresColumns}
        data={features}
        expandableRows={true}
        selectableRowsHighlight
        progressPending={loading}
        expandableRowsComponent={({ data }: { data: InformationPackageFeatureApi }) => (
          <div className="table-folded-content">{data.definition}</div>
        )}
        paginationPerPage={initialPageSize}
        paginationTotalRows={featuresTotal}
        onChangePage={(skip: number, limit: number) => fetchFeatures(skip, limit, featureFilter)}
        onChangeFilter={handleFeatureFilterChange}
        typeSearch="multiselect"
        searchOptions={searchOptions}
      />
    </>
  );
}

export default InformationPackageFeaturesTable;
